import { WithChildren } from '../../../models/children-props';
import { useApolloClient, useReactiveVar } from '@apollo/client';
import {
  changeSelectedWorkspaceId,
  invitationDetailsVar,
  isFromChromeExtension,
  socialOAuthVar
} from '../../../providers/AppProvider';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { GlobalErrorModalContext } from '../../../providers/GlobalErrorModalProvider';
import { getNewAccessToken, useAuth } from '../../../hooks/useAuth';
import { useSearchParams } from 'react-router-dom';
import { GetMeDocument, GetMeQuery } from '../../../models/graphql';
import { ApolloQueryResult } from '@apollo/client/core';
import { getApolloErrorMessage } from '../../../helpers/remapApolloErrors';
import { useDidMount } from '../../../hooks/useDidMount';
import { FullPageLoader } from '../../../components/containers/Loader/Loader.view';
import { showToast } from '../../../helpers/showToast';
import { IInvitation } from '../../../models/auth';

export const AutomaticLoginWrapper = (props: WithChildren): JSX.Element => {
  const socialOAuth = useReactiveVar(socialOAuthVar);
  const { setError } = useContext(GlobalErrorModalContext);
  const { isLoggedIn } = useAuth();
  const calledLogin = useRef(false);
  const [searchParams] = useSearchParams();
  const apolloClient = useApolloClient();
  const isLoggingInFromLegacy = searchParams.get('from') === 'legacy' || searchParams.get('fl') === '1';
  const [attemptingAutoLogin, setAttemptingAutoLogin] = useState(!isLoggedIn && !socialOAuth);
  const invitation: IInvitation | null = useReactiveVar(invitationDetailsVar);

  useEffect(() => {
    if (isLoggingInFromLegacy) {
      localStorage.clear();
      sessionStorage.clear();
    }
  }, [isLoggingInFromLegacy]);

  const attemptAutoLogin = () => {
    if (calledLogin.current) {
      return;
    }
    calledLogin.current = true;
    getNewAccessToken()
      .then(() => {
        if (isFromChromeExtension) {
          window.location.assign('/chrome_extension_loggedin');
          return;
        }
        let afterLoginPath = searchParams.get('r') ? '/' + searchParams.get('r') : '';
        if (invitation) {
          afterLoginPath = '/invite/' + invitation.token;
        }

        apolloClient
          .query({
            query: GetMeDocument,
            fetchPolicy: 'network-only'
          })
          .then((data: ApolloQueryResult<GetMeQuery>) => {
            if (data.data?.me?.organizations[0].workspaces[0].id) {
              changeSelectedWorkspaceId(data.data.me.organizations[0].workspaces[0].id, afterLoginPath);
            }
          })
          .catch(() => {
            changeSelectedWorkspaceId('', afterLoginPath);
          });
      })
      .catch(error => {
        const apolloErrorType = getApolloErrorMessage(error);
        if (apolloErrorType === 'SECURITY_NO_REFRESH_TOKEN' || apolloErrorType === 'SECURITY_REFRESH_TOKEN_INVALID') {
          // If the GQL refresh token is invalid and we're on /login, do a fresh page load to load the legacy portal
          // unless we are using a development server, which would cause an infinite refresh loop.
          // TODO When we only allow logging in through React app, we can get rid of this.
          // TODO This is because we render the legacy login portal for now.
          if (process.env.NODE_ENV === 'development' && !['', '443', '80'].includes(window.location.port)) {
            // Development server detected, show a toast and don't refresh the page which would cause a loop.
            showToast({
              severity: 'warn',
              summary: 'React Login Portal',
              detail:
                'The React login page will only show for development servers - built apps will refresh the page which will show the legacy login portal'
            });
          } else {
            window.location.href = '/login';
            return;
          }
        } else {
          setError(error);
        }
        setAttemptingAutoLogin(false);
      });
  };

  useDidMount(() => {
    if (attemptingAutoLogin) {
      attemptAutoLogin();
    }
  });

  if (attemptingAutoLogin) {
    return <FullPageLoader />;
  }

  return <>{props.children}</>;
};
